/// <amd-module name="Test/Analytics/TestAnalyticsTrendChart/Sources/TestInsightsTrendChartsSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { DataSourceHelper } from "Test/Analytics/Common/Utils/DataSourceHelper";
import { ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";
import * as CommonResources from "Test/Analytics/Common/Resources";

export class TestInsightsTrendChartsSource extends TestReportSource {
    protected _allOutcomes: CommonTypes.TestOutcome[] = [
        CommonTypes.TestOutcome.Passed,
        CommonTypes.TestOutcome.Failed,
        CommonTypes.TestOutcome.Aborted,
        CommonTypes.TestOutcome.Error,
        CommonTypes.TestOutcome.Inconclusive,
        CommonTypes.TestOutcome.NotExecuted,
        CommonTypes.TestOutcome.NotImpacted
    ];

    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_TestInsightsTrendChartsReport", pageContext);
    }

    public queryTrendChartData(
        testReportContext: CommonTypes.ITestReportContext,
        testContext: CommonTypes.ITestContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        switch (DataSourceHelper.getTestInsightTrendChartDataSourceTable(confValues)) {
            case CommonTypes.DataSourceTable.TestResult:
                return this._queryTestOutcomeCountTrendDataStackedByTestOutcome_TestResultTable(testReportContext, testContext, confValues);
            case CommonTypes.DataSourceTable.TestResultDaily:
                return this._queryTestOutcomeCountTrendDataStackedByTestOutcome_TestResultDailyTable(testReportContext, testContext, confValues);
            default:
                throw new Error("Invalid Data source.");
        }
    }

    protected _getTestContextFilter(testIdentifier: string): string {
        return `TestSK eq ${testIdentifier}`;
    }

    protected _getGroupByPeriodColumnString(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return "CompletedDateSK";
            case CommonTypes.TrendBy.Weeks:
                return "CompletedOn/WeekStartingDate";
        }
    }

    protected _getGroupByDailyPeriodColumnString(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return "DateSK";
            case CommonTypes.TrendBy.Weeks:
                return "Date/WeekStartingDate";
        }
    }

    protected _getOutcomeCountAggregateAndComputeStrings(
        outcomes: CommonTypes.TestOutcome[]
    ): { outcomeAggregationStr: string; outcomeComputeStr: string } {
        let outcomeAggregationStr = "";
        let outcomeComputeStr = "";

        if (outcomes.length > 0) {
            outcomes.forEach(o => {
                if (!outcomeAggregationStr) {
                    outcomeAggregationStr = this._getOutcomeCountStringForAggregation(o);
                    outcomeComputeStr = this._getOutcomeComputeStrings(o);
                } else {
                    outcomeAggregationStr += `, ${this._getOutcomeCountStringForAggregation(o)}`;
                    outcomeComputeStr += ` add ${this._getOutcomeComputeStrings(o)}`;
                }
            });
        } else {
            // If no filters specified, AggregateValue is positioned inside the aggregate clause
            outcomeAggregationStr = "ResultCount with sum as AggregateValue";
        }

        return { outcomeAggregationStr: outcomeAggregationStr, outcomeComputeStr: outcomeComputeStr };
    }

    protected _getOutcomeComputeStrings(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return `ResultFailCount`;
            case CommonTypes.TestOutcome.Passed:
                return `ResultPassCount`;
            case CommonTypes.TestOutcome.Aborted:
                return `ResultAbortedCount`;
            case CommonTypes.TestOutcome.Error:
                return `ResultErrorCount`;
            case CommonTypes.TestOutcome.Inconclusive:
                return `ResultInconclusiveCount`;
            case CommonTypes.TestOutcome.NotExecuted:
                return `ResultNotExecutedCount`;
            case CommonTypes.TestOutcome.NotImpacted:
                return `ResultNotImpactedCount`;
            default:
                return "";
        }
    }

    private _queryTestOutcomeCountTrendDataStackedByTestOutcome_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        testContext: CommonTypes.ITestContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            // When outcome filter present then remove it as this is not to be applied as filter predicates.
            const filters = { ...confValues.configuredFilters };
            delete filters[CommonTypes.Filter.Outcome];

            const outcomeCountStrs = this._getOutcomeCountAggregateAndComputeStrings(
                confValues.configuredFilters && confValues.configuredFilters[CommonTypes.Filter.Outcome]
                    ? confValues.configuredFilters[CommonTypes.Filter.Outcome].values.map((val: FilterValueItem) => Number(val.value))
                    : this._allOutcomes
            );

            const filterStr: string =
                `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                    confValues.trendBy,
                    confValues.period
                )} and ` + ` ${this._getTestContextFilter(testContext.testIdentifier)} ${this._getFilterByFieldsString(filters)})`;
            const groupByStr = `groupby((${this._getGroupByDailyPeriodColumnString(confValues.trendBy)}), aggregate(${
                outcomeCountStrs.outcomeAggregationStr
            }))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                if (!data || !data.value) {
                    throw new Error("Data is empty");
                }

                const chartData: ITrendChartData = {
                    primaryChartData: {},
                    confValues: confValues,
                    metricSelected: CommonTypes.Metric.ResultCount,
                    isCachedData: data.isCachedData
                };

                // Iterate over all data
                (data.value as any[]).forEach(d => {
                    const date = this._parseReturnDailyDateIntoDisplayForm(confValues.trendBy, d);

                    // Populate chart data for all outcomes
                    this._populateStackedChartData(d.ResultPassCount, CommonResources.TestOutcome_Passed, date, chartData);
                    this._populateStackedChartData(d.ResultFailCount, CommonResources.TestOutcome_Failed, date, chartData);
                    this._populateStackedChartData(d.ResultAbortedCount, CommonResources.TestOutcome_Aborted, date, chartData);
                    this._populateStackedChartData(d.ResultErrorCount, CommonResources.TestOutcome_Error, date, chartData);
                    this._populateStackedChartData(
                        d.ResultInconclusiveCount,
                        CommonResources.TestOutcome_Inconclusive,
                        date,
                        chartData
                    );
                    this._populateStackedChartData(
                        d.ResultNotExecutedCount,
                        CommonResources.TestOutcome_NotExecuted,
                        date,
                        chartData
                    );
                    this._populateStackedChartData(
                        d.ResultNotImpactedCount,
                        CommonResources.TestOutcome_NotImpacted,
                        date,
                        chartData
                    );
                });

                return chartData;
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountTrendDataStackedByTestOutcome_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        testContext: CommonTypes.ITestContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr: string =
                `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                    confValues.trendBy,
                    confValues.period
                )} and ${this._getTestContextFilter(testContext.testIdentifier)} ` +
                ` ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByStr = `groupby((${this._getGroupByTestPropertiesString(CommonTypes.GroupBy.Outcome)}, ${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}), aggregate($count as AggregateValue))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                if (!data || !data.value) {
                    throw new Error("Data is empty");
                }

                const chartData: ITrendChartData = {
                    primaryChartData: {},
                    confValues: confValues,
                    metricSelected: CommonTypes.Metric.ResultCount,
                    isCachedData: data.isCachedData
                };

                // Iterate over all data
                (data.value as any[]).forEach(d => {
                    const date = this._parseReturnDateIntoDisplayForm(confValues.trendBy, d);
                    const stackByValue = this._parseTestProperties(CommonTypes.GroupBy.Outcome, d).displayValue;

                    if (!chartData.primaryChartData[stackByValue]) {
                        chartData.primaryChartData[stackByValue] = [];
                    }
                    chartData.primaryChartData[stackByValue].push({
                        date: date,
                        aggrValue: d.AggregateValue
                    });
                });

                return chartData;
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _populateStackedChartData(outcomeCount: number, outcomeName: string, date: string, chartData: ITrendChartData): void {
        if (outcomeCount) {
            if (!chartData.primaryChartData[outcomeName]) {
                chartData.primaryChartData[outcomeName] = [];
            }
            chartData.primaryChartData[outcomeName].push({
                date: date,
                aggrValue: outcomeCount
            });
        }
    }
}
